import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, User } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { trackChatOpen, trackChatMessage, trackChatTranscript } from './GoogleAnalytics';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// UI translations
const UI = {
  sl: {
    title: 'Asistent General Ledger',
    sub: 'Odgovori takoj · ekipa govori SL · EN · SR/HR · ES',
    close: 'Zapri klepet',
    typing: 'Piše…',
    handoff: 'Pošljite pogovor naši ekipi — odgovorimo po e-pošti',
    emailPh: 'vas@email.si',
    send: 'Pošlji',
    copy: 'Kopija celotnega pogovora gre na info@general-ledger.eu.',
    inputPh: 'Vprašajte o ustanovitvi, DDV, dovoljenjih, cenah…',
    disclaimer: 'AI asistent · lahko se zmoti',
    human: 'Govori z osebo',
    chips: ['Ustanovitev podjetja', 'Vračilo DDV', 'Delovna dovoljenja', 'Cene', 'Najem pisarne'],
    hello: 'Pozdravljeni! Sem asistent podjetja General Ledger.\nVprašajte me o ustanovitvi podjetja, računovodstvu in DDV, delovnih dovoljenjih, davčnem svetovanju ali najemu pisarn.',
    thanks: (e) => `Hvala! Kopija pogovora je bila poslana na info@general-ledger.eu — ekipa vam bo odgovorila na ${e || 'vaš e-naslov'}.`,
    tipTitle: 'Imate vprašanje?',
    tipText: 'Vprašajte me o ustanovitvi, DDV, dovoljenjih ali cenah — odgovorim takoj.',
  },
  en: {
    title: 'General Ledger Assistant',
    sub: 'Answers instantly · team speaks SL · EN · SR/HR · ES',
    close: 'Close chat',
    typing: 'Typing…',
    handoff: "Send this chat to our team — we'll reply by email",
    emailPh: 'your@email.com',
    send: 'Send',
    copy: 'A copy of the full conversation goes to info@general-ledger.eu.',
    inputPh: 'Ask about setup, VAT, permits, prices…',
    disclaimer: 'AI assistant · may make mistakes',
    human: 'Talk to a person',
    chips: ['Set up a company', 'VAT refunds', 'Work permits', 'Prices', 'Office rental'],
    hello: "Hi! I'm the General Ledger assistant.\nAsk me about company formation, accounting & VAT, work permits, tax advice or office rental.",
    thanks: (e) => `Thank you! A copy of this chat has been sent to info@general-ledger.eu — our team will reply to ${e || 'you'} by email.`,
    tipTitle: 'Questions?',
    tipText: 'Ask me about setup, VAT, permits or prices — I answer instantly.',
  }
};

const ChatWidget = () => {
  const { language } = useLanguage();
  const t = UI[language] || UI.sl;
  
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([{ id: 'welcome', from: 'bot', text: t.hello }]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [showHandoff, setShowHandoff] = useState(false);
  const [handoffEmail, setHandoffEmail] = useState('');
  const [handoffSent, setHandoffSent] = useState(false);
  const [sessionId] = useState(() => `chat_${Date.now()}_${Math.random().toString(36).slice(2)}`);
  const [messageCount, setMessageCount] = useState(0);
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Reset hello message when language changes
  useEffect(() => {
    setMessages((current) => {
      if (current.length === 1 && current[0].id === 'welcome' && current[0].text !== t.hello) {
        return [{ id: 'welcome', from: 'bot', text: t.hello }];
      }
      return current;
    });
  }, [t.hello]);

  const sendMessage = async (text) => {
    if (!text.trim() || isTyping || messageCount >= 30) return;
    
    const userMsg = text.trim();
    setMessages(prev => [...prev, { id: `${sessionId}-${prev.length}`, from: 'user', text: userMsg }]);
    setInput('');
    setIsTyping(true);
    setMessageCount(prev => prev + 1);
    trackChatMessage(); // GA4 tracking

    try {
      const response = await fetch(`${API_URL}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: userMsg,
          session_id: sessionId,
          language: language
        })
      });

      if (!response.ok) throw new Error('Chat API error');

      const data = await response.json();
      
      setMessages(prev => [...prev, { id: `${sessionId}-${prev.length}`, from: 'bot', text: data.response }]);
      
      if (data.show_handoff) {
        setShowHandoff(true);
      }
    } catch {
      setMessages(prev => [...prev, { 
        id: `${sessionId}-${prev.length}`,
        from: 'bot', 
        text: language === 'sl' 
          ? 'Oprostite, prišlo je do napake. Prosim poskusite znova ali nas kontaktirajte na info@general-ledger.eu.'
          : 'Sorry, an error occurred. Please try again or contact us at info@general-ledger.eu.'
      }]);
    } finally {
      setIsTyping(false);
    }
  };

  const sendTranscript = async () => {
    if (!handoffEmail.trim()) return;

    try {
      const transcript = messages.map(m => 
        `${m.from === 'bot' ? 'Asistent' : 'Obiskovalec'}: ${m.text}`
      ).join('\n\n');

      const response = await fetch(`${API_URL}/api/chat/transcript`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: handoffEmail,
          transcript: transcript,
          session_id: sessionId,
          language: language
        })
      });

      if (response.ok) {
        setHandoffSent(true);
        trackChatTranscript(); // GA4 tracking
        setMessages(prev => [...prev, { id: `${sessionId}-${prev.length}`, from: 'bot', text: t.thanks(handoffEmail) }]);
      }
    } catch {
      setMessages(prev => [...prev, {
        id: `${sessionId}-${prev.length}`,
        from: 'bot',
        text: language === 'sl' ? 'Prepisa ni bilo mogoče poslati. Pišite nam na info@general-ledger.eu.' : 'The transcript could not be sent. Email us at info@general-ledger.eu.'
      }]);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  return (
    <div id="gl-chat" data-testid="chat-widget">
      {!isOpen ? (
        <>
          {/* Tip bubble - hidden on screens under 1200px */}
          <div className="glc-tip hidden xl:block">
            <strong>{t.tipTitle}</strong> {t.tipText}
          </div>
          {/* Launch button */}
          <button
            onClick={() => { setIsOpen(true); trackChatOpen(); }}
            className="glc-launch"
            aria-label={language === 'sl' ? 'Odpri klepet z asistentom General Ledger' : 'Open chat with General Ledger assistant'}
            data-testid="chat-launch-btn"
          >
            <MessageCircle className="w-7 h-7" />
            <span className="glc-dot"></span>
          </button>
        </>
      ) : (
        <div className="glc-box" role="dialog" aria-label={t.title}>
          {/* Header */}
          <div className="glc-head">
            <div className="w-10 h-10 border border-[#C9A462] text-[#C9A462] flex items-center justify-center font-serif text-[21px] relative flex-shrink-0">
              GL
              <span className="glc-dot" style={{ bottom: '-3px', right: '-3px', top: 'auto' }}></span>
            </div>
            <div className="flex-1 flex flex-col gap-0.5">
              <span className="text-[15px] font-bold">{t.title}</span>
              <span className="text-[12px] text-[#B9B3A7]">{t.sub}</span>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="w-11 h-11 flex items-center justify-center text-[#B9B3A7] text-[26px] hover:text-white"
              aria-label={t.close}
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Messages */}
          <div className="glc-list">
            {messages.map((msg) => (
              <div key={msg.id} className={msg.from === 'bot' ? 'glc-bot' : 'glc-user'} data-testid={`chat-message-${msg.id}`}>
                {msg.text}
              </div>
            ))}
            {isTyping && <div className="glc-bot glc-typing">{t.typing}</div>}
            <div ref={messagesEndRef} />
          </div>

          {/* Quick chips */}
          {messages.filter(m => m.from === 'user').length < 2 && (
            <div className="glc-chips">
              {t.chips.map((chip) => (
                <button key={chip} onClick={() => sendMessage(chip)} data-testid={`chat-chip-${chip.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                  {chip}
                </button>
              ))}
            </div>
          )}

          {/* Handoff form */}
          {showHandoff && !handoffSent && (
            <div className="glc-hand">
              <label className="text-[12.5px] font-bold text-[#15181C]">{t.handoff}</label>
              <div className="flex gap-1.5">
                <input
                  type="email"
                  placeholder={t.emailPh}
                  value={handoffEmail}
                  onChange={(e) => setHandoffEmail(e.target.value)}
                  className="flex-1 h-[42px] border border-[#C9BFAD] bg-[#FFFDF8] px-2.5 text-[14px]"
                  data-testid="handoff-email"
                />
                <button
                  onClick={sendTranscript}
                  className="h-[42px] px-3.5 bg-[#0E1114] text-[#F6F2EA] font-bold text-[13px]"
                  data-testid="handoff-send"
                >
                  {t.send}
                </button>
              </div>
              <span>{t.copy}</span>
            </div>
          )}

          {/* Input */}
          <div className="glc-input">
            <input
              ref={inputRef}
              type="text"
              placeholder={t.inputPh}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={messageCount >= 30}
              data-testid="chat-input"
            />
            <button 
              onClick={() => sendMessage(input)}
              disabled={!input.trim() || isTyping}
              aria-label={language === 'sl' ? 'Pošlji sporočilo' : 'Send message'}
              data-testid="chat-send-btn"
            >
              <Send className="w-[18px] h-[18px]" />
            </button>
          </div>

          {/* Footer */}
          <div className="glc-foot">
            <span>{t.disclaimer} · <a href="/zasebnost" className="underline">{language === 'sl' ? 'zasebnost' : 'privacy'}</a></span>
            <button
              onClick={() => {
                setShowHandoff(true);
                sendMessage(language === 'sl' ? 'Želim govoriti z osebo' : 'I would like to talk to a person');
              }}
              className="font-bold text-[#7A5B28] underline"
            >
              {t.human}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatWidget;
